import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fix default marker icons (Leaflet + webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const RADIUS_PRESETS = [20, 40, 80, 150, 300];

const DEFAULT_CENTER = [40.3925, 49.8825]; // Baku center

// Component that listens for clicks on the map
function ClickPicker({ onPick }) {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

// Component to pan map to new coords
function MapMover({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      map.setView([lat, lng], 17, { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}

export const RestaurantSecurity = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [ipInput, setIpInput] = useState('');
  const [myIp, setMyIp] = useState('');

  useEffect(() => {
    loadAll();
  }, [restaurantId]);

  const loadAll = async () => {
    try {
      const [r, ipRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}/client-ip`, { withCredentials: true }).catch(() => ({ data: { ip: '' } })),
      ]);
      setRestaurant(r.data);
      setMyIp(ipRes.data.ip || '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const patch = async (updates, msg = '✅ Yadda saxlanıldı') => {
    setSaving(true);
    setSavedMsg('');
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/restaurants/${restaurantId}/security`,
        updates,
        { withCredentials: true }
      );
      setRestaurant(data);
      setSavedMsg(msg);
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      setSavedMsg('❌ ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Brauzeriniz lokasiyanı dəstəkləmir');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        patch({ geo_lat: pos.coords.latitude, geo_lng: pos.coords.longitude }, '📍 Cari mövqe yadda saxlanıldı');
      },
      () => alert('Lokasiya icazəsi rədd edildi'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const addCurrentIP = async () => {
    const existing = restaurant.wifi_allowed_ips || [];
    if (!myIp) {
      alert('IP-niz aşkarlana bilmədi');
      return;
    }
    if (existing.includes(myIp)) {
      alert('Bu IP artıq əlavə edilib');
      return;
    }
    await patch({ wifi_allowed_ips: [...existing, myIp] }, `✅ ${myIp} əlavə edildi`);
  };

  const addIP = async () => {
    const ip = ipInput.trim();
    if (!ip) return;
    const existing = restaurant.wifi_allowed_ips || [];
    if (existing.includes(ip)) {
      alert('Bu IP artıq əlavə edilib');
      return;
    }
    await patch({ wifi_allowed_ips: [...existing, ip] }, `✅ ${ip} əlavə edildi`);
    setIpInput('');
  };

  const removeIP = async (ip) => {
    const existing = restaurant.wifi_allowed_ips || [];
    await patch({ wifi_allowed_ips: existing.filter((i) => i !== ip) }, '✅ Silindi');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-stone-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" /></div>;
  }
  if (!restaurant) return <div className="p-10 text-red-600">Restoran tapılmadı</div>;

  const lat = restaurant.geo_lat ?? DEFAULT_CENTER[0];
  const lng = restaurant.geo_lng ?? DEFAULT_CENTER[1];
  const radius = restaurant.geo_radius_m || 40;
  const hasGeo = restaurant.geo_lat != null && restaurant.geo_lng != null;

  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <button
          onClick={() => navigate(`/restaurant/${restaurantId}`)}
          className="text-emerald-600 hover:underline mb-4"
          data-testid="security-back-btn"
        >← Restoran ayarları</button>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-4">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-stone-900" data-testid="security-title">🛡️ Sifariş Qoruması</h1>
              <p className="text-sm text-stone-500 mt-1">{restaurant.name}</p>
            </div>
            {savedMsg && <span className="text-sm font-medium text-emerald-700" data-testid="security-saved-msg">{savedMsg}</span>}
          </div>
        </div>

        {/* GEOLOCATION */}
        <SecurityCard
          icon="🌍"
          title="Geolokasiya yoxlanışı"
          subtitle="Müştəri restoran ərazisindən kənardadırsa sifariş bloklanır"
          enabled={!!restaurant.geo_enabled}
          onToggle={(v) => patch({ geo_enabled: v })}
          testId="geo"
        >
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={useMyLocation}
                disabled={saving}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                data-testid="use-my-location-btn"
              >📍 Cari mövqemi istifadə et</button>
              {hasGeo && (
                <span className="text-xs text-stone-600 self-center" data-testid="geo-coords">
                  📌 {lat.toFixed(6)}, {lng.toFixed(6)}
                </span>
              )}
            </div>

            <div className="h-72 rounded-xl overflow-hidden border border-stone-300" data-testid="map-container">
              <MapContainer
                center={[lat, lng]}
                zoom={hasGeo ? 17 : 13}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap"
                />
                {hasGeo && <Marker position={[lat, lng]} />}
                {hasGeo && <Circle center={[lat, lng]} radius={radius} pathOptions={{ color: '#10b981', fillColor: '#10b981', fillOpacity: 0.15 }} />}
                <ClickPicker onPick={(la, ln) => patch({ geo_lat: la, geo_lng: ln }, '📍 Mövqe yeniləndi')} />
                {hasGeo && <MapMover lat={lat} lng={lng} />}
              </MapContainer>
            </div>
            <p className="text-xs text-stone-500">💡 Restoran mövqeyini dəqiqləşdirmək üçün xəritədə klikləyin və ya &quot;Cari mövqemi istifadə et&quot; düyməsi.</p>

            <div>
              <label className="block text-xs font-bold text-stone-700 uppercase mb-2">Maksimum radius</label>
              <div className="grid grid-cols-5 gap-2">
                {RADIUS_PRESETS.map((r) => (
                  <button
                    key={r}
                    onClick={() => patch({ geo_radius_m: r })}
                    disabled={saving}
                    className={`py-2 rounded-lg text-sm font-bold border-2 ${radius === r ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-stone-200 text-stone-600'}`}
                    data-testid={`radius-preset-${r}`}
                  >{r}m</button>
                ))}
              </div>
              <input
                type="number"
                min="10"
                max="2000"
                value={radius}
                onChange={(e) => patch({ geo_radius_m: parseInt(e.target.value, 10) || 40 })}
                className="mt-2 w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                data-testid="radius-input"
              />
            </div>
          </div>
        </SecurityCard>

        {/* WORKING HOURS */}
        <SecurityCard
          icon="🕐"
          title="İş saatları"
          subtitle="Bu saatlardan kənar QR sifariş qəbul etmir"
          enabled={!!restaurant.hours_enabled}
          onToggle={(v) => patch({ hours_enabled: v })}
          testId="hours"
        >
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-stone-700 uppercase mb-1">Açılış</label>
              <input
                type="time"
                value={restaurant.hours_open || '09:00'}
                onChange={(e) => patch({ hours_open: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                data-testid="hours-open-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-700 uppercase mb-1">Bağlanış</label>
              <input
                type="time"
                value={restaurant.hours_close || '23:00'}
                onChange={(e) => patch({ hours_close: e.target.value })}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg"
                data-testid="hours-close-input"
              />
            </div>
          </div>
          <p className="text-xs text-stone-500 mt-2">Baku vaxtı (UTC+4). Gecə yarı keçən saatlar dəstəklənir (məs: 18:00-02:00).</p>
        </SecurityCard>

        {/* WIFI / IP allowlist */}
        <SecurityCard
          icon="📶"
          title="Wi-Fi şəbəkəsi yoxlanışı"
          subtitle="Yalnız restoran şəbəkəsindən sifariş qəbul edilir"
          enabled={!!restaurant.wifi_enabled}
          onToggle={(v) => patch({ wifi_enabled: v })}
          testId="wifi"
        >
          <div className="space-y-3">
            {myIp && (
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 flex items-center justify-between text-sm">
                <span><strong>Sizin cari IP:</strong> <code className="font-mono">{myIp}</code></span>
                <button
                  onClick={addCurrentIP}
                  disabled={saving}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50"
                  data-testid="add-current-ip-btn"
                >＋ Bu IP-ni əlavə et</button>
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={ipInput}
                onChange={(e) => setIpInput(e.target.value)}
                placeholder="məs: 192.168.1.1 və ya 192.168.1.0/24"
                className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm font-mono"
                data-testid="ip-input"
              />
              <button
                onClick={addIP}
                disabled={!ipInput.trim()}
                className="px-4 py-2 bg-stone-800 hover:bg-stone-900 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                data-testid="add-ip-btn"
              >Əlavə et</button>
            </div>

            <div className="space-y-1.5">
              {(restaurant.wifi_allowed_ips || []).length === 0 ? (
                <p className="text-xs text-stone-500 italic">Hələ IP əlavə edilməyib</p>
              ) : (
                restaurant.wifi_allowed_ips.map((ip) => (
                  <div key={ip} className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2" data-testid={`ip-row-${ip}`}>
                    <code className="text-sm font-mono">{ip}</code>
                    <button
                      onClick={() => removeIP(ip)}
                      className="text-red-600 hover:text-red-700 text-sm font-bold"
                      data-testid={`remove-ip-${ip}`}
                    >🗑</button>
                  </div>
                ))
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              💡 <strong>İpucu:</strong> Hər müştəri telefonu fərqli IP göstərə bilər (mobil internet). Buna görə bu metod
              ən yaxşı <strong>yalnız Wi-Fi-da</strong> işləyir. Restoranınızın xarici (public) IP-sini bilirsinizsə, onu əlavə edin.
            </div>
          </div>
        </SecurityCard>

        {/* CONFIRMATION */}
        <SecurityCard
          icon="👨‍🍳"
          title="Ofisiant / Sahib təsdiqi"
          subtitle="Hər sifariş təsdiqdən keçdikdən sonra mətbəxə gedir (ofisiant və ya sahib təsdiq edir)"
          enabled={!!restaurant.require_confirmation}
          onToggle={(v) => patch({ require_confirmation: v })}
          testId="confirmation"
        >
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            ⚡ <strong>Necə işləyir:</strong> Müştəri sifariş verəndə status <strong>&quot;Təsdiq gözləyir&quot;</strong> olur.
            Həm <strong>ofisiant paneli</strong>, həm də <strong>mətbəx ekranı</strong>nda dərhal görünür.
            Birinci kim cavab versə sifariş ya təsdiqlənir, ya rədd edilir. Saxta sifariş üçün ən güclü qoruma.
          </div>
        </SecurityCard>

      </div>
    </div>
  );
};

const SecurityCard = ({ icon, title, subtitle, enabled, onToggle, children, testId }) => (
  <div className={`bg-white rounded-2xl shadow-sm border-2 p-5 mb-4 transition ${enabled ? 'border-emerald-300' : 'border-stone-200'}`} data-testid={`security-card-${testId}`}>
    <div className="flex items-start justify-between gap-4 mb-3">
      <div className="flex items-start gap-3">
        <span className="text-3xl">{icon}</span>
        <div>
          <h3 className="text-lg font-bold text-stone-900">{title}</h3>
          <p className="text-sm text-stone-500 mt-0.5">{subtitle}</p>
        </div>
      </div>
      <button
        onClick={() => onToggle(!enabled)}
        className={`relative inline-flex h-7 w-14 items-center rounded-full transition flex-shrink-0 ${enabled ? 'bg-emerald-500' : 'bg-stone-300'}`}
        data-testid={`security-toggle-${testId}`}
      >
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${enabled ? 'translate-x-8' : 'translate-x-1'}`} />
        <span className={`absolute text-[9px] font-bold ${enabled ? 'left-1.5 text-white' : 'right-1.5 text-stone-600'}`}>{enabled ? 'ON' : 'OFF'}</span>
      </button>
    </div>
    {enabled && <div className="mt-4 pt-4 border-t border-stone-100">{children}</div>}
  </div>
);
